import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Avatar } from '@heroui/react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { UserCircleIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon, BuildingOffice2Icon } from '@/icons';

/**
 * Account menu: avatar trigger opening user identity + quick links + sign-out.
 * Sign-out calls the real logout endpoint via AuthContext.
 */
export function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success('Signed out');
    navigate('/login', { replace: true });
  };

  const initials = (user?.displayName ?? user?.email ?? '?')
    .split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <button type="button" className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand" aria-label="Account menu">
          <Avatar
            size="sm"
            name={initials}
            src={user?.avatarUrl ?? undefined}
            className="cursor-pointer bg-brand/15 text-brand"
          />
        </button>
      </DropdownTrigger>
      <DropdownMenu aria-label="Account" disabledKeys={['profileHeader']}>
        <DropdownItem key="profileHeader" isReadOnly className="h-auto opacity-100" textValue="Signed in">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-fg">{user?.displayName ?? 'Account'}</span>
            <span className="truncate text-xs text-fg-muted">{user?.email}</span>
            {user?.tenantName && (
              <span className="mt-1 flex items-center gap-1 text-xs text-fg-subtle">
                <BuildingOffice2Icon className="h-3.5 w-3.5" /> {user.tenantName}
              </span>
            )}
          </div>
        </DropdownItem>
        <DropdownItem key="profile" startContent={<UserCircleIcon className="h-4 w-4" />} onPress={() => navigate('/settings/profile')}>
          Profile
        </DropdownItem>
        <DropdownItem key="settings" startContent={<Cog6ToothIcon className="h-4 w-4" />} onPress={() => navigate('/settings/profile')}>
          Settings
        </DropdownItem>
        <DropdownItem
          key="logout"
          className="text-danger"
          color="danger"
          startContent={<ArrowRightOnRectangleIcon className="h-4 w-4" />}
          onPress={handleLogout}
        >
          Sign out
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
